import { useEffect, useState } from "react";
import { Accessibility, X, EyeOff } from "lucide-react";

type Settings = {
  textScale: number;
  contrast: boolean;
  underlineLinks: boolean;
  reduceMotion: boolean;
  readableFont: boolean;
  spacing: boolean;
  bigCursor: boolean;
  hidden: boolean;
};

const DEFAULTS: Settings = {
  textScale: 100,
  contrast: false,
  underlineLinks: false,
  reduceMotion: false,
  readableFont: false,
  spacing: false,
  bigCursor: false,
  hidden: false,
};

const KEY = "momentum.a11y";
const DISABLED_KEY = "momentum.a11y.disabled";
const DISABLED_COOKIE = "momentum_a11y_off";
const SHOW_EVENT = "momentum:show-accessibility";
const DISABLED_EVENT = "momentum:a11y-disabled-changed";

function readCookieFlag() {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c === `${DISABLED_COOKIE}=1`);
}

function writeCookieFlag(disabled: boolean) {
  if (typeof document === "undefined") return;
  document.cookie = disabled
    ? `${DISABLED_COOKIE}=1; path=/; max-age=31536000; samesite=lax`
    : `${DISABLED_COOKIE}=; path=/; max-age=0; samesite=lax`;
}

export function isAccessibilityDisabled() {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(DISABLED_KEY) === "1") return true;
  } catch {
    /* storage unavailable */
  }
  return readCookieFlag();
}

export function setAccessibilityDisabled(disabled: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (disabled) localStorage.setItem(DISABLED_KEY, "1");
    else localStorage.removeItem(DISABLED_KEY);
  } catch {
    /* storage unavailable — cookie still carries the preference */
  }
  writeCookieFlag(disabled);
  window.dispatchEvent(new Event(DISABLED_EVENT));
}


function apply(s: Settings) {
  const root = document.documentElement;
  root.style.setProperty("--a11y-text-scale", `${s.textScale / 100}`);
  root.classList.toggle("a11y-contrast", s.contrast);
  root.classList.toggle("a11y-underline", s.underlineLinks);
  root.classList.toggle("a11y-reduce-motion", s.reduceMotion);
  root.classList.toggle("a11y-readable-font", s.readableFont);
  root.classList.toggle("a11y-spacing", s.spacing);
  root.classList.toggle("a11y-big-cursor", s.bigCursor);
}

const SCALES = [100, 115, 130, 150];

export function showAccessibilityMenu() {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(KEY);
    const next = { ...DEFAULTS, ...(raw ? (JSON.parse(raw) as Partial<Settings>) : {}) };
    next.hidden = false;
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
  setAccessibilityDisabled(false);
  window.dispatchEvent(new Event(SHOW_EVENT));

}


export function AccessibilityMenu() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [disabled, setDisabled] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const next = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
        setSettings(next);
        apply(next);
      }
    } catch {
      /* ignore malformed settings */
    }
    setDisabled(isAccessibilityDisabled());
    setMounted(true);

    const onShow = () => {
      setDisabled(false);
      setSettings((prev) => {
        const next = { ...prev, hidden: false };
        apply(next);
        try {
          localStorage.setItem(KEY, JSON.stringify(next));
        } catch {
          /* storage unavailable */
        }
        return next;
      });
    };
    const onDisabledChange = () => {
      const off = isAccessibilityDisabled();
      setDisabled(off);
      if (off) {
        setOpen(false);
        apply(DEFAULTS);
      } else {
        setSettings((prev) => {
          apply(prev);
          return prev;
        });
      }
    };
    // Re-check the saved preference whenever the page is restored from
    // bfcache, refocused, or changed in another tab.
    const recheck = () => onDisabledChange();
    window.addEventListener(SHOW_EVENT, onShow);
    window.addEventListener(DISABLED_EVENT, onDisabledChange);
    window.addEventListener("pageshow", recheck);
    window.addEventListener("focus", recheck);
    window.addEventListener("storage", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener(SHOW_EVENT, onShow);
      window.removeEventListener(DISABLED_EVENT, onDisabledChange);
      window.removeEventListener("pageshow", recheck);
      window.removeEventListener("focus", recheck);
      window.removeEventListener("storage", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);



  const update = (patch: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      apply(next);
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  };

  const reset = () => {
    setSettings(DEFAULTS);
    apply(DEFAULTS);
    localStorage.removeItem(KEY);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!mounted || disabled) return null;

  const toggles: { key: keyof Settings; label: string; hint: string }[] = [

    { key: "contrast", label: "High contrast", hint: "Stronger text and border contrast" },
    { key: "underlineLinks", label: "Underline links", hint: "Links never rely on colour alone" },
    { key: "reduceMotion", label: "Reduce motion", hint: "Stops animation and fades" },
    { key: "readableFont", label: "Readable font", hint: "Heavier, wider letterforms" },
    { key: "spacing", label: "More spacing", hint: "Extra letter and line spacing" },
    { key: "bigCursor", label: "Large cursor", hint: "Bigger pointer target" },
  ];

  if (settings.hidden) {
    return (
      <button
        type="button"
        onClick={() => update({ hidden: false })}
        aria-label="Show accessibility options"
        style={{ bottom: "calc(1.25rem + var(--consent-offset, 0px))" }}
        className="fixed left-5 z-[75] flex h-8 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs text-muted-foreground shadow-lg transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <Accessibility className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">A11y</span>
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Accessibility options"
        aria-expanded={open}
        aria-controls="a11y-panel"
        style={{ bottom: "calc(1.25rem + var(--consent-offset, 0px))" }}
        className="fixed left-5 z-[75] flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition-colors hover:border-foreground/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <Accessibility className="h-5 w-5" aria-hidden="true" />
      </button>

      {open && (
        <div
          id="a11y-panel"
          role="dialog"
          aria-label="Accessibility options"
          style={{ bottom: "calc(5rem + var(--consent-offset, 0px))" }}
          className="fixed left-5 z-[75] max-h-[70vh] w-[min(22rem,calc(100vw-2.5rem))] overflow-y-auto rounded-sm border border-border bg-card p-5 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="micro-label">Accessibility</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                These settings are saved on this device and apply to every page.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility options"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <fieldset className="mt-6">
            <legend className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Text size
            </legend>
            <div className="mt-3 flex gap-2">
              {SCALES.map((scale) => (
                <button
                  key={scale}
                  type="button"
                  onClick={() => update({ textScale: scale })}
                  aria-pressed={settings.textScale === scale}
                  className={`min-h-11 flex-1 rounded-sm border px-2 text-sm transition-colors ${
                    settings.textScale === scale
                      ? "border-foreground/60 bg-secondary text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {scale}%
                </button>
              ))}
            </div>
          </fieldset>

          <ul className="mt-6 space-y-1.5">
            {toggles.map((t) => {
              const on = settings[t.key] as boolean;
              return (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => update({ [t.key]: !on } as Partial<Settings>)}
                    aria-pressed={on}
                    className="flex min-h-11 w-full items-center justify-between gap-4 rounded-sm border border-transparent px-3 py-2 text-left transition-colors hover:border-border"
                  >
                    <span>
                      <span className="block text-sm text-foreground">{t.label}</span>
                      <span className="block text-xs text-muted-foreground">{t.hint}</span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors ${
                        on ? "border-primary bg-primary/30" : "border-border bg-secondary"
                      }`}
                    >
                      <span
                        className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full transition-all ${
                          on ? "left-6 bg-primary" : "left-1 bg-muted-foreground"
                        }`}
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={() => update({ hidden: true })}
            className="mt-5 flex min-h-11 w-full items-center justify-center gap-2 rounded-sm border border-border text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <EyeOff className="h-4 w-4" aria-hidden="true" />
            Hide this menu
          </button>

          <button
            type="button"
            onClick={reset}
            className="mt-3 min-h-11 w-full rounded-sm border border-border text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Reset to default
          </button>
        </div>
      )}
    </>
  );
}
